/*!
 * url.js — url(): an element that is a link
 * © 2026 Rob Canning — GPLv3
 *
 *   url(role:viola)                          join this project as viola
 *   url(part:"Part: Viola")                  this project, that part
 *   url(role:conductor, part:all)            a name, and the whole score
 *   url(project:quartet, role:"violin 1")    another project
 *   url(href:"https://oscilla.cc", target:new)   anywhere, in a new tab
 *
 * The same thing a link drawn in Inkscape (Object > Link) does, written in
 * the DSL so it can be edited in the cue editor. role / part / project make
 * an Oscilla address (?project=…&role=…&part=…, see layerFilter.js for what
 * a link does to a part chosen by hand); href is used as it is and wins over
 * them. Not named link(): that is the signal binding, link(fader1.t, …).
 *
 * Not a playhead cue: registered at load like display() and always live. A
 * press follows it -- except while the cue editor is on, when a press on the
 * element is for choosing it.
 */

const unquote = (v) => (v == null ? null : String(v).trim().replace(/^[`'"]+|[`'"]+$/g, ""));

/** The address a url() cue goes to */
export function urlCueHref(args = []) {
  const get = (k) => unquote(args.find((a) => a.type === k)?.value);
  const href = get("href");
  if (href) return href;
  const q = new URLSearchParams();
  q.set("project", get("project") || window.currentProjectName || "");
  for (const k of ["role", "part"]) {
    const v = get(k);
    if (v) q.set(k, v);
  }
  return `${location.pathname}?${q.toString().replace(/\+/g, "%20")}`;
}

export function handleUrlCue(el, args = []) {
  if (!el) return;
  const href = urlCueHref(args);
  const target = unquote(args.find((a) => a.type === "target")?.value);
  const newTab = target === "new" || target === "_blank";

  // a rescan replaces the handler rather than adding a second
  if (el._oscillaUrl) el.removeEventListener("click", el._oscillaUrl);
  el._oscillaUrl = (e) => {
    if (window.dslInspectorActive) return;      // choosing it in the editor
    e.preventDefault();
    e.stopPropagation();
    if (newTab) window.open(href, "_blank", "noopener");
    else location.href = href;
  };
  el.addEventListener("click", el._oscillaUrl);
  // the score's scrub leaves it alone (transportTouch.js), and it looks like a link
  el.setAttribute("data-cue-clickable", "url");
  el.style.cursor = "pointer";
  // not on text: a <title> inside a text or tspan joins its words (textContent)
  if (!/^(text|tspan|textPath)$/.test(el.tagName) && !el.querySelector(":scope > title")) {
    const t = document.createElementNS("http://www.w3.org/2000/svg", "title");
    t.textContent = href;
    el.prepend(t);
  }
}
